import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Alert,
  Pagination,
  Chip,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import { getPositionIncomeHistoryV2 } from '../../api/wallet';
import { formatValue } from '../../utils/utils';

const IncomeHistoryModal = ({ open, onClose, position, currencyView }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const limit = 10;

  const handleOpenCashIn = (cashInId) => {
    handleClose();
    navigate(`/cashin/list?highlight=${cashInId}`);
  };

  const fetchIncomeHistory = useCallback(async () => {
    if (!position?.ID) return;

    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit,
      };

      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await getPositionIncomeHistoryV2(position.ID, currencyView, params);
      setData(response.data);
    } catch (err) {
      setError('Erro ao buscar historico de rendimentos.');
    } finally {
      setLoading(false);
    }
  }, [position?.ID, page, startDate, endDate]);

  useEffect(() => {
    if (open && position?.ID) {
      fetchIncomeHistory();
    }
  }, [open, position?.ID, fetchIncomeHistory]);

  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, startDate, endDate]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleClose = () => {
    setPage(1);
    setStartDate('');
    setEndDate('');
    setData(null);
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Historico de Rendimentos - {position?.Asset?.Name || 'Posicao'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Data Inicio"
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              label="Data Fim"
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
          </Box>

          {data && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label={`Total: ${data.total} registro(s)`}
                size="small"
                variant="outlined"
              />
            </Box>
          )}
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !data || data.items.length === 0 ? (
          <Alert severity="info">
            Nenhum rendimento encontrado para esta posicao.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Descricao</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Valor ({currencyView})</TableCell>
                    <TableCell align="center" width={50}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{formatDate(item.received_date)}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.category?.Name || '-'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatValue(item.value, item.currency)}
                      </TableCell>
                      <TableCell align="right">
                        {formatValue(item.value_in_currency, currencyView)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Abrir na lista de receitas">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenCashIn(item.id)}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IncomeHistoryModal;
